import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import { SALARY_BASE } from '../api';
import { CabinetEmptyState, CabinetListFooter, CabinetListToolbar } from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import { useCabinetSalary } from '../queries';
import type { CabinetSalaryRecord } from '../types';

const useSalaryUi = createCabinetViewStore('cabinet.salary:ui', 'table');
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type SalaryColumn = 'effective_date' | 'amount' | 'base_amount';

const fmtDate = (d: string) => new Date(d).toLocaleDateString();

/** The operative record = the latest-effective one (max effective_date) — the
 * same record the backend uses to drive payroll/rate prefill (`latest_salary_record`),
 * so it's marked "Current" even if it's a scheduled future raise. Robust to sort
 * order/pagination (computed from the loaded rows). */
function currentRecordId(rows: CabinetSalaryRecord[]): number | null {
  if (rows.length === 0) return null;
  return rows.reduce((a, b) => (a.effective_date >= b.effective_date ? a : b)).id;
}

export function CabinetSalaryPage() {
  const view = useSalaryUi((s) => s.view);
  const setView = useSalaryUi((s) => s.setView);
  const ordering = useSalaryUi((s) => s.ordering);
  const setOrdering = useSalaryUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useCabinetSalary({
    page,
    search: search || undefined,
    ordering,
  });
  const rows = useMemo(() => data?.results ?? [], [data]);
  const currentId = useMemo(() => currentRecordId(rows), [rows]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">My Salary</h1>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        search={{
          value: searchInput,
          onChange: (v) => {
            setPage(1);
            setSearchInput(v);
          },
          placeholder: 'Search note…',
        }}
        exportBase={SALARY_BASE}
        exportParams={{ search: search || undefined, ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <SalaryTable
          rows={rows}
          currentId={currentId}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <SalaryList rows={rows} currentId={currentId} />
      ) : view === 'cards' ? (
        <SalaryCards rows={rows} currentId={currentId} />
      ) : (
        <SalaryGroupedByYear rows={rows} currentId={currentId} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['record', 'records']}
      />
    </div>
  );
}

function SalaryTable({
  rows,
  currentId,
  ordering,
  onOrderingChange,
}: {
  rows: CabinetSalaryRecord[];
  currentId: number | null;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<SalaryColumn>
              column="effective_date"
              value={ordering}
              onChange={onOrderingChange}
            >
              Effective
            </SortableTableHead>
            <SortableTableHead<SalaryColumn>
              column="amount"
              value={ordering}
              onChange={onOrderingChange}
              className="text-right"
            >
              Amount
            </SortableTableHead>
            <SortableTableHead<SalaryColumn>
              column="base_amount"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden text-right sm:table-cell"
            >
              Base (USD)
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No salary records yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((r) => {
              const isCurrent = r.id === currentId;
              return (
                <TableRow key={r.public_id} className={cn(isCurrent && 'bg-accent/40')}>
                  <TableCell>
                    <span className="inline-flex items-center gap-2">
                      {fmtDate(r.effective_date)}
                      {isCurrent ? <Badge>Current</Badge> : null}
                    </span>
                  </TableCell>
                  <TableCell className={cn('text-right tabular-nums', isCurrent && 'font-semibold')}>
                    {formatMoney(r.amount, r.currency) ?? '—'}
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums sm:table-cell">
                    {formatMoney(r.base_amount, 'USD') ?? '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground hidden md:table-cell">
                    {r.note || '—'}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function SalaryList({ rows, currentId }: { rows: CabinetSalaryRecord[]; currentId: number | null }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((r) => {
        const isCurrent = r.id === currentId;
        return (
          <li
            key={r.public_id}
            className={cn('flex items-center gap-3 px-3 py-2 text-sm', isCurrent && 'bg-accent/40')}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2 truncate font-semibold">
              {fmtDate(r.effective_date)}
              {isCurrent ? <Badge>Current</Badge> : null}
            </span>
            <span className="tabular-nums">{formatMoney(r.amount, r.currency) ?? '—'}</span>
            <span className="text-muted-foreground hidden truncate sm:inline">{r.note || ''}</span>
          </li>
        );
      })}
    </ul>
  );
}

function SalaryCards({ rows, currentId }: { rows: CabinetSalaryRecord[]; currentId: number | null }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((r) => {
        const isCurrent = r.id === currentId;
        return (
          <Card key={r.public_id} className={cn(isCurrent && 'ring-primary/50 ring-1')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                {formatMoney(r.amount, r.currency) ?? '—'}
                {isCurrent ? <Badge>Current</Badge> : null}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-1 text-xs">
              <p>Effective {fmtDate(r.effective_date)}</p>
              <p>Base (USD): {formatMoney(r.base_amount, 'USD') ?? '—'}</p>
              {r.note ? <p>{r.note}</p> : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function SalaryGroupedByYear({
  rows,
  currentId,
}: {
  rows: CabinetSalaryRecord[];
  currentId: number | null;
}) {
  const groups = useMemo(() => {
    const byYear = new Map<string, CabinetSalaryRecord[]>();
    for (const r of rows) {
      const year = String(new Date(r.effective_date).getFullYear());
      (byYear.get(year) ?? byYear.set(year, []).get(year)!).push(r);
    }
    return [...byYear.entries()].sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [rows]);

  if (rows.length === 0) return <EmptyText />;

  return (
    <div className="space-y-6">
      {groups.map(([year, bucket]) => (
        <section key={year}>
          <h2 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
            {year}
          </h2>
          <SalaryCards rows={bucket} currentId={currentId} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No salary records yet.</CabinetEmptyState>;
}
